import React from 'react';

import { v4 as uuid } from 'uuid';

import { BaseProps, BaseDropdown } from '../Dropdown/Dropdown';

type ItemSchema = {
  label: string;
  value: string;
  isDisabled?: string;
  isItemDisabled?: string;
  isValueDisabled?: string;
  isHidden?: string;
};

type OwnProps = {
  schema?: ItemSchema;
  value?: any[];
  onChange: (items: any[]) => void;
  initialValues?: any[];
  truncateSelectedItems?: boolean;
  hideSelectedOptions?: boolean;
  items?: any[];
};

type Props<T extends unknown> = OwnProps & Omit<BaseProps<T>, 'items'>;

export const Multiselect = <T,>({
  items = [],
  name = `MultiselectField-${uuid()}`,
  value = [],
  onChange,
  placeholder = 'Select Option',
  initialValues = [],
  schema = {
    label: 'label',
    value: 'value',
    isDisabled: 'isDisabled',
    isItemDisabled: 'isItemDisabled',
    isValueDisabled: 'isValueDisabled',
    isHidden: 'isHidden',
  },
  isSearchable = true,
  errors,
  invalid,
  loadOptions,
  noOptionsMessage,
  onCreateOption,
  ...props
}: Props<T>) => {
  const [selectedValues, setSelectedValues] = React.useState<any[]>([]);

  function constructOption(item: any) {
    return {
      label: item[schema.label],
      value: item[schema.value],
      isDisabled: schema?.isDisabled ? item[schema.isDisabled] : undefined,
      isValueDisabled: schema?.isValueDisabled
        ? item[schema.isValueDisabled]
        : undefined,
      isItemDisabled: schema?.isItemDisabled
        ? item[schema.isItemDisabled]
        : undefined,
      isHidden: schema?.isHidden ? item[schema.isHidden] : undefined,
    };
  }

  function onReturnOption(options: any) {
    if (!options) {
      onChange([]);

      if (loadOptions) {
        setSelectedValues([]);
      }

      return;
    }

    if (loadOptions) {
      onChange(options);
      setSelectedValues(options);
    } else {
      const selectedOptions = items.filter(item =>
        options.find(
          (option: any) =>
            item[schema.value as keyof typeof item] === option.value
        )
      );

      onChange(selectedOptions);
    }
  }

  const isInvalid = !!invalid || !!(errors && errors.length > 0);

  return (
    <BaseDropdown
      value={loadOptions ? selectedValues : value}
      name={name}
      isMulti
      initialValue={initialValues}
      placeholder={placeholder}
      onChange={onReturnOption}
      closeMenuOnSelect={!!loadOptions}
      isSearchable={isSearchable}
      items={items && items.map(constructOption)}
      defaultOptions={
        loadOptions ? selectedValues.map(constructOption) : undefined
      }
      errors={errors}
      invalid={isInvalid}
      loadOptions={loadOptions}
      noOptionsMessage={noOptionsMessage}
      onCreateOption={onCreateOption}
      {...props}
    />
  );
};
